import { useEffect, useState } from 'react'

import { Modal } from '@mantine/core'

import Button from 'src/components/Buttons/Button/Button'
import type { VoEProvider } from 'src/graphql/types/carriers'
import type { VoECompletionMethod } from 'src/graphql/types/verificationOfEmployments'

import ReadOnlyField from './ReadOnlyField'
import VoECompletionFields, {
  EMPTY_VOE_COMPLETION,
  isVoECompletionValid,
  toVoECompletionInput,
} from './VoECompletionFields'
import type {
  VoECompletionContact,
  VoECompletionSubject,
  VoECompletionValue,
} from './VoECompletionFields'

export interface CompletionMethodValues {
  completionMethod: VoECompletionMethod
  platform: VoEProvider | null
  contact?: Partial<VoECompletionContact> | null
  dotNumber?: string | null
  note?: string | null
}

interface CompletionMethodModalProps {
  opened: boolean
  onClose: () => void
  onSubmit: (values: CompletionMethodValues) => void
  loading?: boolean
  title?: string
  /** Explains what completing does, shown above the method buttons. */
  description?: React.ReactNode
  submitLabel?: string
  cancelLabel?: string
  /** Pre-selected platform for an ATS completion (the carrier's preferred one). */
  defaultPlatform?: VoEProvider | null
  allowNoResponse?: boolean
  applicantName?: string | null
  companyName?: string | null
  /** Omitted for a bulk change. */
  subject?: VoECompletionSubject | null
}

const EMPTY: VoECompletionValue = EMPTY_VOE_COMPLETION

const CompletionMethodModal = ({
  opened,
  onClose,
  onSubmit,
  loading = false,
  title = 'Employment Verification',
  description,
  submitLabel = 'Complete',
  cancelLabel = 'Cancel',
  defaultPlatform = null,
  allowNoResponse = true,
  applicantName = null,
  companyName = null,
  subject = null,
}: CompletionMethodModalProps) => {
  const [value, setValue] = useState<VoECompletionValue>(EMPTY)

  // Start every opening fresh; the platform pre-fill follows the VoE shown.
  useEffect(() => {
    if (opened) setValue(EMPTY)
  }, [opened])

  const handleClose = () => {
    setValue(EMPTY)
    onClose()
  }

  const handleSubmit = () => {
    if (!isVoECompletionValid(value)) return
    onSubmit(toVoECompletionInput(value) as CompletionMethodValues)
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="sm"
      closeOnClickOutside={false}
      withinPortal
      // Explicit zIndex instead of stackId: this modal is opened from within the
      // VoE details drawer, so it lives in Modal.Stack while the drawer lives in
      // the separate Drawer.Stack — both default to z-index 200, so the drawer
      // (painted later) covered the modal. zIndex={210} renders it above
      // stack-managed drawers (~201+). Mantine ignores an explicit zIndex when a
      // stackId is also set, so stackId must stay removed. Mirrors EmailSenderModal.
      zIndex={210}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
      }}
    >
      <div className="flex flex-col gap-4">
        {description && (
          <div className="text-sm text-doubleNickel-gray-600">
            {description}
          </div>
        )}
        {applicantName && (
          <ReadOnlyField label="Applicant Name" value={applicantName} />
        )}
        {companyName && (
          <ReadOnlyField label="Company Name" value={companyName} />
        )}
        <VoECompletionFields
          value={value}
          onChange={setValue}
          defaultPlatform={defaultPlatform}
          allowNoResponse={allowNoResponse}
          subject={subject}
          disabled={loading}
        />

        <div className="flex flex-row justify-end gap-3 border-t border-doubleNickel-gray-200 pt-3">
          <Button
            text={cancelLabel}
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          />
          <Button
            text={submitLabel}
            onClick={handleSubmit}
            loading={loading}
            disabled={!isVoECompletionValid(value)}
          />
        </div>
      </div>
    </Modal>
  )
}

export default CompletionMethodModal
